<template>
    <div class="wa-container">
        <div class="wa-header">
            <div class="wa-header__title">
                Lista invitați
            </div>
        </div>

        <p class="wa-desc">
            Apasă butonul
            <font-awesome-icon icon="fa-brands fa-whatsapp" class="text-green-500" />
            de lângă fiecare invitat pentru a trimite invitația prin WhatsApp.
            Se va deschide o conversație cu mesajul pre-completat.
        </p>

        <div v-if="filteredParticipants.length" class="wa-list">
            <div
                v-for="participant in filteredParticipants"
                :key="participant.invitation_id"
                class="wa-item"
                :class="{ 'wa-item--sent': isShared(participant) }"
            >
                <div class="wa-item__info text-left">
                    <div class="wa-item__name">
                        {{ participant.recipient_surname }} {{ participant.recipient_name }}
                    </div>
                    <div class="wa-item__phone">{{ participant.recipient_mobile }}</div>
                </div>
                <div class="wa-item__status" v-if="isShared(participant)">
                    <font-awesome-icon icon="fa-solid fa-check" class="text-green-500" />
                    <span class="text-green-600 text-xs font-semibold">Trimis {{ formatSharedTime(participant) }}</span>
                </div>
                <a
                    :href="buildWhatsAppLink(participant)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="wa-item__btn"
                    @click="markSent(participant)"
                >
                    <font-awesome-icon icon="fa-brands fa-whatsapp" />
                    {{ isShared(participant) ? 'Retrimite' : 'Trimite' }}
                </a>
            </div>
        </div>

        <p
            v-else
            class="text-gray-500 mt-4 px-5"
        >
            Nu există invitați cu număr de telefon în acest proiect.
        </p>

        <div class="wa-summary" v-if="filteredParticipants.length">
            <div class="wa-summary__count">
                ai trimis
                <span class="font-bold text-green-600">{{ sharedCount }}</span> / {{ filteredParticipants.length }}
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { mapGetters } from 'vuex';

export default {
    name: 'GetParticipantsListSendWhatsApp',
    props: {
        projectId: {
            required: true,
        },
        isSendWhatsAppModalOpen: {
            type: Boolean,
            required: true,
        },
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            sessionSent: new Set(),
        };
    },
    computed: {
        ...mapGetters(['scopedProjectData']),
        filteredParticipants() {
            if (!this.scopedProjectData || !this.scopedProjectData.participants) return [];
            return this.scopedProjectData.participants.filter(
                (p) => p.recipient_mobile && p.recipient_mobile.trim() !== ''
            );
        },
        sharedCount() {
            return this.filteredParticipants.filter((p) => this.isShared(p)).length;
        },
    },
    watch: {
        isSendWhatsAppModalOpen(newValue) {
            if (newValue) {
                const projectId = this.$route.params.projectId;
                const options = { mobile: true };
                this.$store.dispatch('fetchProjectUsers', {
                    projectId,
                    options,
                    participants: true,
                });
                this.sessionSent = new Set();
            }
        },
    },
    methods: {
        getBaseURL() {
            const url = new URL(window.location.href);
            return url.origin;
        },
        buildInvitationURL(participant) {
            const base = this.getBaseURL();
            const pd = this.scopedProjectData;
            const projectId = pd.project_id;
            const nameInUrl = pd.name_in_url;
            const slug = pd.project_slug || '';

            if (pd.template_type === 'simple') {
                const safeSlug = slug.toLowerCase().trim().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
                return nameInUrl
                    ? `${base}/#/ps/${projectId}/${safeSlug}`
                    : `${base}/#/ps/${projectId}`;
            } else {
                const uniqueParam = participant.unique_parameter;
                const safeSlugComplex = slug.toLowerCase().trim().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
                return nameInUrl
                    ? `${base}/#/public/${projectId}/${uniqueParam}/${safeSlugComplex}`
                    : `${base}/#/public/${projectId}/${uniqueParam}`;
            }
        },
        buildWhatsAppLink(participant) {
            const pd = this.scopedProjectData;
            const message = pd.invitation_message_whatsapp || pd.invitation_message || '';
            const url = this.buildInvitationURL(participant);
            const fullMessage = message ? `${message} ${url}` : url;

            let phone = participant.recipient_mobile.replace(/\s+/g, '').replace(/[^0-9+]/g, '');
            if (phone.startsWith('0')) {
                phone = '4' + phone;
            }
            if (!phone.startsWith('+')) {
                phone = '+' + phone;
            }

            return `https://wa.me/${phone}?text=${encodeURIComponent(fullMessage)}`;
        },
        isShared(participant) {
            return !!participant.whatsapp_shared_time || this.sessionSent.has(participant.unique_parameter);
        },
        formatSharedTime(participant) {
            const time = participant.whatsapp_shared_time;
            if (!time) return '';
            const d = new Date(time);
            return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        },
        async markSent(participant) {
            const newSet = new Set(this.sessionSent);
            newSet.add(participant.unique_parameter);
            this.sessionSent = newSet;

            try {
                const jwtToken = localStorage.getItem('userToken');
                const res = await axios.post(
                    '/api/index.php?endpoint=markWhatsAppShared',
                    {
                        project_id: this.projectId,
                        unique_parameter: participant.unique_parameter,
                    },
                    { headers: { Authorization: `Bearer ${jwtToken}` } }
                );
                if (res.data.success && res.data.shared_time) {
                    participant.whatsapp_shared_time = res.data.shared_time;
                }
            } catch (e) {
                console.error('Failed to record WhatsApp share:', e);
            }
        },
    },
};
</script>

<style scoped>
.wa-container {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}
.wa-header {
    padding: 8px 0 0;
}
.wa-header__title {
    font-size: 28px;
    font-weight: 400;
    margin-bottom: 8px;
}
.wa-desc {
    padding: 0 20px;
    margin-bottom: 12px;
    font-size: 14px;
    color: #64748b;
    line-height: 1.6;
}
@media (max-width: 640px) {
    .wa-desc {
        padding: 0 8px;
        font-size: 13px;
    }
}
@media (min-width: 768px) {
    .wa-header__title {
        font-size: 36px;
    }
}
.wa-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 0 20px;
    max-height: 400px;
    overflow-y: auto;
}
@media (max-width: 640px) {
    .wa-list {
        padding: 0 8px;
    }
}

.wa-item {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    padding: 12px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    transition: all 0.15s;
}
@media (max-width: 640px) {
    .wa-item {
        padding: 10px 12px;
    }
}
.wa-item:hover {
    border-color: #22c55e;
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.08);
}
.wa-item--sent {
    background: #f0fdf4;
    border-color: #bbf7d0;
}

.wa-item__info {
    flex: 1 1 0;
    min-width: 120px;
}
.wa-item__name {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
}
.wa-item__phone {
    font-size: 12px;
    color: #64748b;
}

.wa-item__status {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    font-size: 11px;
    white-space: nowrap;
}
@media (max-width: 640px) {
    .wa-item__status {
        order: 3;
        width: 100%;
        justify-content: flex-end;
    }
}

.wa-item__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    background: #22c55e;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.15s;
    flex-shrink: 0;
    white-space: nowrap;
}
.wa-item__btn:hover {
    background: #16a34a;
    color: #fff;
}

.wa-summary {
    padding: 16px 20px;
    text-align: right;
}
.wa-summary__count {
    font-size: 13px;
    color: #64748b;
}
</style>
